import {
  getRouteApi,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { format as formatTZ, toZonedTime } from "date-fns-tz";
import { useEffect, useMemo } from "react";
import type {
  DateRangeChangeEvent,
  SelectedFilters,
} from "@/components/discovery/discovery.types";
import DiscoveryViewControlPanel from "@/components/discovery/discovery-content-control-panels/DiscoveryViewControlPanel";
import DiscoveryViewModals from "@/components/discovery/modals/DiscoveryViewModals";
import { PageHeader } from "@/components/layouts/PageHeader";
import { TabPanel } from "@/components/ui/tabs/Tab";
import type { NewsFeedItem } from "@/data-access/news-feeds/queries/news-feeds-queries";
import { mergeSearchWithSavedViewClear } from "@/hooks/useMergeSearchNavigate";
import { resolveFeedSwitchHref } from "@/lib/auth/feedLastLocationUtils";
import {
  calculateDateRangeFromDays,
  formatDateForUrl,
  parseDateString,
} from "@/lib/utils/dateUtils";
import { buildAndFromSelected } from "@/lib/utils/filters";
import { logoutUser } from "@/store/auth.async-actions";
import useAuth, { type AuthState } from "@/store/auth.slice";
import { DiscoveryProvider } from "@/store/discovery/discovery-params.context";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import useNewsFeedsStore from "@/store/news-feeds/news-feeds.slice";

// Get typed access to the discovery feed route
const routeApi = getRouteApi("/_authenticated/discovery/$feedId");

/** Map store feeds to NewsFeedItem for PageHeader switcher. */
const toNewsFeedItems = (
  feeds: Array<{ id: string | number; name: string; image?: string | null }>,
): NewsFeedItem[] =>
  feeds.map((f) => ({
    id: String(f.id),
    name: f.name,
    image: f.image ?? null,
  }));

/**
 * Computes effective start/end dates from search params.
 * If custom dates provided, use those. Otherwise calculate from filterDays.
 */
const computeDates = (
  filterDays: number,
  startDateParam?: string,
  endDateParam?: string,
): { startDate: string; endDate: string; isCustomDaysRange: boolean } => {
  const timeZone = "UTC";

  // Check if custom dates are provided
  if (startDateParam && endDateParam) {
    return {
      startDate: startDateParam,
      endDate: endDateParam,
      isCustomDaysRange: true,
    };
  }

  // Calculate from filterDays
  const { startDate, endDate } = calculateDateRangeFromDays(filterDays);
  return {
    startDate: formatTZ(toZonedTime(startDate, timeZone), "yyyy-MM-dd", {
      timeZone,
    }),
    endDate: formatTZ(toZonedTime(endDate, timeZone), "yyyy-MM-dd", {
      timeZone,
    }),
    isCustomDaysRange: false,
  };
};

/**
 * Discovery route component for TanStack Router.
 * Uses TanStack Router search params as source of truth for filters.
 */
const DiscoveryRoute = () => {
  const { feedId } = routeApi.useParams();
  const searchParams = routeApi.useSearch();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (s) => s.location.pathname,
    structuralSharing: true,
  });
  const feeds = useNewsFeedsStore((state) => state.allNewsFeeds);
  const setFeedId = useNewsFeedsStore((state) => state.setFeedId);
  const feedItems = toNewsFeedItems(feeds);
  const user = useAuth((state: AuthState) => state.user);
  const selectedKeyMessageFilterExclusion = useKeyMessageStore(
    (state) => state.selectedKeyMessageFilterExclusion,
  );
  const updateSelectedKeyMessageFilterExclusion = useKeyMessageStore(
    (state) => state.updateSelectedKeyMessageFilterExclusion,
  );

  // Sync route feedId to store so hooks that fall back to currentFeedId (e.g. SavedViewsList) stay aligned with the URL
  useEffect(() => {
    setFeedId(feedId);
    // Avoid AND from a previous feed applying if URL `selected` ever survives a feed change
    updateSelectedKeyMessageFilterExclusion("OR");
  }, [feedId, setFeedId, updateSelectedKeyMessageFilterExclusion]);

  // Compute dates from search params
  const { startDate, endDate, isCustomDaysRange } = computeDates(
    searchParams.filterDays,
    searchParams.startDate,
    searchParams.endDate,
  );

  // Handle date range changes - update URL search params
  const handleDateRangeChange = (range: DateRangeChangeEvent) => {
    if (range.isCustom && range.from && range.to) {
      navigate({
        to: pathname,
        search: (prev) =>
          mergeSearchWithSavedViewClear(prev, {
            startDate: formatDateForUrl(range.from as Date),
            endDate: formatDateForUrl(range.to as Date),
            filterDays: prev.filterDays,
          }),
      });
    } else if (range.days) {
      navigate({
        to: pathname,
        search: (prev) =>
          mergeSearchWithSavedViewClear(prev, {
            filterDays: range.days as number,
            startDate: undefined,
            endDate: undefined,
          }),
      });
    }
  };

  // Context value for nested components (table, cell renderers).
  // Memoized so that hover-triggered router state updates (preload) do not
  // produce a new object reference and re-render all context consumers.
  const selectedFilters = useMemo(() => {
    const andCategories =
      selectedKeyMessageFilterExclusion === "AND"
        ? new Set(["KEY_MESSAGE"])
        : undefined;
    return buildAndFromSelected(
      searchParams.selected,
      andCategories,
    ) as SelectedFilters;
  }, [searchParams.selected, selectedKeyMessageFilterExclusion]);

  const contextValue = useMemo(
    () => ({
      feedId,
      startDate,
      endDate,
      selectedFilters,
    }),
    [feedId, startDate, endDate, selectedFilters],
  );

  return (
    <DiscoveryProvider value={contextValue}>
      <DiscoveryViewModals />
      <PageHeader
        label="Discovery"
        showFeedSwitcher
        feeds={feedItems}
        selectedFeedId={feedId}
        onFeedChange={(id) => {
          setFeedId(id);
          navigate({
            href: resolveFeedSwitchHref(
              id,
              "DISCOVERY",
              user.feed_last_locations,
            ),
          });
        }}
        userEmail={user?.email ?? ""}
        userTenantName={user?.default_tenant?.name}
        onLogout={logoutUser}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        {/* Control panel: tabs + date (nav v2) or search + date (legacy); tabs rendered inside panel when tabsBasePath set */}
        <div className="w-full shrink-0 bg-gray-50">
          <DiscoveryViewControlPanel
            filterDays={searchParams.filterDays}
            isCustomDaysRange={isCustomDaysRange}
            startDate={parseDateString(searchParams.startDate)}
            endDate={parseDateString(searchParams.endDate)}
            onDateRangeChange={handleDateRangeChange}
            tabsBasePath={`/discovery/${feedId}`}
          />
        </div>

        <div className="min-h-0 flex-1 px-8 flex flex-col pb-10 bg-white">
          <TabPanel>
            <Outlet />
          </TabPanel>
        </div>
      </div>
    </DiscoveryProvider>
  );
};

export default DiscoveryRoute;
